<script lang="ts" setup>
import { reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { EmailChangeRequest, EmailChangeRequested, EmailChangeVerify } from '@/types'
import { STATUS_CODES } from '@/constants'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useJwtStore, useNotifyStore } from '@/stores'
import { LOADING_SIZES, LOADING_WIDTH, uuid } from '@/composables'

const props = defineProps<{
  modelValue: boolean
  currentEmail: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'success'): void
}>()

const { t } = useI18n()
const router = useRouter()
const jwtStore = useJwtStore()
const notifyStore = useNotifyStore()

const state = reactive({
  step: 'email' as 'email' | 'otp',
  newEmail: '',
  otp: '',
  currentFactor: 'email' as EmailChangeRequested['current_factor'],
  currentOtp: '',
  loading: false,
  error: ''
})

// Reset state when modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      state.step = 'email'
      state.newEmail = ''
      state.otp = ''
      state.currentOtp = ''
      state.error = ''
      state.loading = false
    }
  }
)

const closeModal = () => {
  emit('update:modelValue', false)
}

const requestEmailChange = async () => {
  if (!state.newEmail) return

  state.loading = true
  state.error = ''

  try {
    const res = await useAxios<EmailChangeRequest, EmailChangeRequested>(
      'POST',
      '/account/email/request',
      true,
      { email: state.newEmail }
    )

    if (res && res.status === STATUS_CODES.OK) {
      const parsed = EmailChangeRequested.safeParse(res.data)
      state.currentFactor = parsed.success ? parsed.data.current_factor : 'email'
      state.otp = ''
      state.currentOtp = ''
      state.step = 'otp'
    }
  } catch (error: unknown) {
    handleAxiosError(error, { logPrefix: 'EmailChangeRequest' })
    const axiosError = error as { response?: { status?: number } }
    if (axiosError.response?.status === 400) {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.sameEmail')
    } else if (axiosError.response?.status === 403) {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.emailNotVerified')
    } else if (axiosError.response?.status === 409) {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.emailInUse')
    } else if (axiosError.response?.status === 429) {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.tooManyRequests')
    } else {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.generic')
    }
  } finally {
    state.loading = false
  }
}

const verifyEmailChange = async () => {
  if (!state.otp || !state.currentOtp) return

  state.loading = true
  state.error = ''

  try {
    const res = await useAxios<EmailChangeVerify, null>(
      'POST',
      '/account/email/verify',
      true,
      { otp: state.otp, current_otp: state.currentOtp }
    )

    if (res && res.status === STATUS_CODES.OK) {
      jwtStore.removeJwt()
      notifyStore.createNotify({
        id: uuid(),
        alertType: 'success',
        title: t('dashboard.account.stammdaten.emailChange.success'),
        message: t('dashboard.account.stammdaten.emailChange.successMessage')
      })
      emit('success')
      router.push({ name: 'login' })
    }
  } catch (error: unknown) {
    handleAxiosError(error, { logPrefix: 'EmailChangeVerify' })
    const axiosError = error as { response?: { status?: number; data?: { error?: string } } }
    if (axiosError.response?.status === 400) {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.noPendingChange')
    } else if (axiosError.response?.status === 401) {
      if (axiosError.response.data?.error !== 'current_factor_invalid') {
        state.error = t('dashboard.account.stammdaten.emailChange.errors.invalidOtp')
      } else if (state.currentFactor === 'totp') {
        state.error = t('dashboard.account.stammdaten.emailChange.errors.currentTotpInvalid')
      } else {
        state.error = t('dashboard.account.stammdaten.emailChange.errors.currentCodeInvalid')
      }
    } else if (axiosError.response?.status === STATUS_CODES.GONE) {
      state.step = 'email'
      state.error = t('dashboard.account.stammdaten.emailChange.errors.changeExpired')
    } else if (axiosError.response?.status === 429) {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.tooManyRequests')
    } else {
      state.error = t('dashboard.account.stammdaten.emailChange.errors.generic')
    }
  } finally {
    state.loading = false
  }
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="440"
    :scrim="true"
    scrim-class="modal-scrim"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="modal-container">
      <div class="modal-header">
        <h3 class="modal-title">
          {{
            state.step === 'email'
              ? $t('dashboard.account.stammdaten.emailChange.title')
              : $t('dashboard.account.stammdaten.emailChange.verifyTitle')
          }}
        </h3>
        <button type="button" class="modal-close" @click="closeModal">
          <v-icon icon="mdi-close" size="20" />
        </button>
      </div>

      <div class="modal-body">
        <!-- Step 1: Enter new email -->
        <div v-if="state.step === 'email'">
          <div class="modal-info">
            <p class="current-email-label">
              {{ $t('dashboard.account.stammdaten.emailChange.currentEmail') }}:
            </p>
            <p class="current-email-value">{{ currentEmail }}</p>
          </div>

          <div class="modal-form-group">
            <label class="form-label">
              {{ $t('dashboard.account.stammdaten.emailChange.newEmail') }}
            </label>
            <input
              v-model="state.newEmail"
              type="email"
              class="form-input"
              :placeholder="$t('dashboard.account.stammdaten.emailChange.newEmailPlaceholder')"
              :disabled="state.loading"
              @keyup.enter="requestEmailChange"
            />
          </div>
        </div>

        <!-- Step 2: Enter OTP with logout warning -->
        <div v-else>
          <!-- Logout Warning -->
          <div class="logout-warning">
            <div class="warning-icon">
              <v-icon icon="mdi-alert-outline" size="20" />
            </div>
            <div class="warning-content">
              <strong>{{ $t('dashboard.account.stammdaten.emailChange.logoutWarning') }}</strong>
              <p>{{ $t('dashboard.account.stammdaten.emailChange.logoutWarningText') }}</p>
            </div>
          </div>

          <p class="modal-description">
            {{
              $t('dashboard.account.stammdaten.emailChange.verifyDescription', {
                email: state.newEmail
              })
            }}
          </p>

          <div class="modal-form-group">
            <input
              v-model="state.otp"
              type="text"
              class="form-input otp-input"
              :placeholder="$t('dashboard.account.stammdaten.emailChange.otpPlaceholder')"
              :disabled="state.loading"
              maxlength="6"
              @keyup.enter="verifyEmailChange"
            />
          </div>

          <div class="modal-form-group">
            <label class="form-label">
              {{
                state.currentFactor === 'totp'
                  ? $t('dashboard.account.stammdaten.emailChange.currentCodeTotp')
                  : $t('dashboard.account.stammdaten.emailChange.currentCodeEmail', { email: currentEmail })
              }}
            </label>
            <input
              v-model="state.currentOtp"
              type="text"
              class="form-input otp-input"
              :placeholder="$t('dashboard.account.stammdaten.emailChange.otpPlaceholder')"
              :disabled="state.loading"
              maxlength="6"
              autocomplete="one-time-code"
              @keyup.enter="verifyEmailChange"
            />
            <p class="modal-hint">{{ $t('dashboard.account.stammdaten.emailChange.currentCodeHint') }}</p>
          </div>
        </div>

        <!-- Error Message -->
        <Transition name="slide-fade">
          <div v-if="state.error" class="modal-error">
            <v-icon icon="mdi-alert-circle" size="18" />
            <span>{{ state.error }}</span>
          </div>
        </Transition>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="state.loading"
          @click="closeModal"
        >
          {{ $t('dashboard.account.stammdaten.emailChange.cancel') }}
        </button>
        <button
          v-if="state.step === 'email'"
          type="button"
          class="btn btn-primary"
          :disabled="state.loading || !state.newEmail"
          @click="requestEmailChange"
        >
          <v-progress-circular
            v-if="state.loading"
            indeterminate
            :size="LOADING_SIZES.button"
            :width="LOADING_WIDTH"
          />
          <v-icon v-else icon="mdi-arrow-right" size="18" />
          <span>{{ $t('dashboard.account.stammdaten.emailChange.requestChange') }}</span>
        </button>
        <button
          v-else
          type="button"
          class="btn btn-success"
          :disabled="state.loading || !state.otp || !state.currentOtp"
          @click="verifyEmailChange"
        >
          <v-progress-circular
            v-if="state.loading"
            indeterminate
            :size="LOADING_SIZES.button"
            :width="LOADING_WIDTH"
          />
          <v-icon v-else icon="mdi-check" size="18" />
          <span>{{ $t('dashboard.account.stammdaten.emailChange.verify') }}</span>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.modal-container {
  background: linear-gradient(145deg, rgba(20, 30, 60, 0.98), rgba(15, 23, 48, 0.98));
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(200, 218, 255, 0.8);
  }
}

.modal-body {
  padding: 24px;
}

.modal-info {
  margin-bottom: 20px;
}

.current-email-label {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0 0 4px;
}

.current-email-value {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 500;
}

.modal-description {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.7);
  margin: 0 0 20px;
  line-height: 1.5;
}

.modal-form-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.modal-hint {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 6px 0 0;
}

.form-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #00b0ff;
    background: rgba(0, 176, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 176, 255, 0.1);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(120, 163, 255, 0.08);
    color: rgba(255, 255, 255, 0.6);
    cursor: not-allowed;
  }
}

.otp-input {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;

  &:not(:placeholder-shown) {
    letter-spacing: 0.3em;
  }
}

/* Logout Warning */
.logout-warning {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.25);
  border-radius: 10px;
}

.warning-icon {
  flex-shrink: 0;
  color: #ffb74d;
  margin-top: 2px;
}

.warning-content {
  flex: 1;

  strong {
    display: block;
    margin-bottom: 4px;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.95);
  }

  p {
    margin: 0;
    font-size: 0.8rem;
    line-height: 1.4;
    color: rgba(200, 218, 255, 0.7);
  }
}

.modal-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 10px;
  color: #ef5350;
  font-size: 0.85rem;
  margin-top: 16px;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: rgba(0, 176, 255, 0.15);
  color: #00b0ff;
  border: 1px solid rgba(0, 176, 255, 0.3);

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.25);
    box-shadow: 0 0 20px rgba(0, 176, 255, 0.15);
  }
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(200, 218, 255, 0.7);
  border: 1px solid rgba(120, 163, 255, 0.15);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

.btn-success {
  background: rgba(76, 175, 80, 0.15);
  color: #66bb6a;
  border: 1px solid rgba(76, 175, 80, 0.3);

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.15);
  }
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Light Theme */
.v-theme--lightTheme {
  .modal-container {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
    border-color: rgba(28, 110, 227, 0.15);
  }

  .modal-header {
    border-color: rgba(28, 110, 227, 0.1);
  }

  .modal-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .modal-close {
    color: rgba(15, 23, 42, 0.5);

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: rgba(15, 23, 42, 0.8);
    }
  }

  .current-email-label {
    color: rgba(15, 23, 42, 0.5);
  }

  .current-email-value {
    color: rgba(15, 23, 42, 0.9);
  }

  .modal-description {
    color: rgba(15, 23, 42, 0.7);
  }

  .form-label {
    color: rgba(15, 23, 42, 0.6);
  }

  .modal-hint {
    color: rgba(15, 23, 42, 0.4);
  }

  .form-input {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.95);

    &::placeholder {
      color: rgba(15, 23, 42, 0.3);
    }

    &:focus {
      border-color: #1c6ee3;
      background: rgba(28, 110, 227, 0.03);
      box-shadow: 0 0 0 3px rgba(28, 110, 227, 0.1);
    }
  }

  .logout-warning {
    background: rgba(255, 152, 0, 0.08);
    border-color: rgba(255, 152, 0, 0.2);

    .warning-content strong {
      color: rgba(15, 23, 42, 0.95);
    }

    .warning-content p {
      color: rgba(15, 23, 42, 0.7);
    }
  }

  .modal-footer {
    border-color: rgba(28, 110, 227, 0.1);
  }

  .btn-primary {
    background: rgba(28, 110, 227, 0.1);
    color: #1c6ee3;
    border-color: rgba(28, 110, 227, 0.2);

    &:hover:not(:disabled) {
      background: rgba(28, 110, 227, 0.15);
    }
  }

  .btn-secondary {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(15, 23, 42, 0.7);
    border-color: rgba(28, 110, 227, 0.15);
  }
}

/* Responsive */
@media (max-width: 480px) {
  .modal-footer {
    flex-direction: column-reverse;

    .btn {
      width: 100%;
      justify-content: center;
    }
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .form-input,
  .btn,
  .modal-close,
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }
}
</style>
